<template>
  <div id="multicastGroupsList">
    <card :title="$t('a-multicastGroups:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-4">
            <label for="filterMcAddr">{{ $t('a-multicastGroups:mcAddr') | capitalize }}</label>
            <input id="filterMcAddr"
                   v-model="filterMcAddr"
                   v-validate="'text'"
                   :data-vv-as="$t('a-multicastGroups:mcAddr')"
                   :class="{ 'is-invalid': errors.has('filterMcAddr') }"
                   type="text"
                   name="filterMcAddr"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('filterMcAddr') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="filterName">{{ $t('a-multicastGroups:name') | capitalize }}</label>
            <input id="filterName"
                   v-model="filterName"
                   v-validate="'text'"
                   :data-vv-as="$t('a-multicastGroups:name')"
                   :class="{ 'is-invalid': errors.has('filterName') }"
                   type="text"
                   name="filterName"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('filterName') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="filterCluster">{{ $t('a-multicastGroups:cluster') | capitalize }}</label>
            <select id="filterCluster" v-model="filterCluster" name="filterCluster" class="form-control">
              <option value="" />
              <option v-for="(c, indexCluster) in $store.getters.allClusters" :key="indexCluster" :value="c">{{ c.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('a-multicastGroups:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="multicastGroupsGrid"
          :title="$t('a-multicastGroups:multicastGroups')"
          :columns="columns"
          :paginated-dto="multicastGroups"
          @load="filter"
    >
      <div slot="name" slot-scope="props" :data-multicastGroup-id="props.dto.id" :data-multicastGroup-name="props.dto.name" class="d-flex">
        <div class="mr-2"><i class="icon-multicastGroup no-pointer" /></div>
        {{ props.dto.name }}
      </div>

      <div slot="actions" slot-scope="props" :data-multicastGroup-id="props.dto.id" class="text-right">
        <i :id="'editMulticastGroup-' + props.dto.id" class="icon-edit" @click="editMulticastGroup(props.dto)" />
        <i :id="'deleteMulticastGroup-' + props.dto.id" class="icon-trash" @click="confirmDeleteMulticastGroup(props.dto)" />
      </div>
    </grid>

    <confirm id="deleteMulticastGroup"
             v-model="showDeleteMulticastGroupConfirm"
             :message="$t('a-multicastGroups:confirmDelete', {multicastGroup: multicastGroupToDelete.name})"
             @confirm:validate="deleteMulticastGroup"
    />

    <floating-button :click-handler="() => { showMulticastGroupForm=true }" icon-classes="icon-add" />
    <multicast-groups-form v-model="showMulticastGroupForm" :multicast-group-to-edit="multicastGroupToEdit" @success="filter" @cancel="multicastGroupToEdit={}" />
  </div>
</template>

<script>
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

import MulticastGroupsForm from './MulticastGroupsForm'

export default {
  titleKey: 'administrationMulticastGroups',

  components: { MulticastGroupsForm },

  data () {
    return {
      // filter
      filterMcAddr: '',
      filterName: '',
      filterCluster: '',
      // List
      multicastGroups: {},
      showDeleteMulticastGroupConfirm: false,
      multicastGroupToDelete: {},
      multicastGroupToEdit: {},
      // Add/edit fleet
      showMulticastGroupForm: false
    }
  },

  computed: {
    columns () {
      return [
        { text: this.$t('a-multicastGroups:mcAddr'), formatter: multicastGroup => multicastGroup.mcAddr, sort: 'mcAddr' },
        { text: this.$t('a-multicastGroups:name'), slot: 'name', sort: 'name' },
        { text: this.$t('a-multicastGroups:cluster'), formatter: multicastGroup => (multicastGroup.cluster ? multicastGroup.cluster.name : '') },
        { slot: 'actions' }
      ]
    }
  },

  created () {
    this.filter()
    this.$store.dispatch('getAllClusters')
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add criteria
      if (this.filterMcAddr) search = this.$utils.addSearch(search, 'like', '*' + this.filterMcAddr + '*', 'mcAddr')
      if (this.filterName) search = this.$utils.addSearch(search, 'like', '*' + this.filterName + '*', 'name')
      if (this.filterCluster) search = this.$utils.addSearch(search, 'eq', this.filterCluster.id, 'cluster.id')

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getMulticastGroups(options)
    },

    async getMulticastGroups (options) {
      const params = this.$utils.getParams({ sort: '+mcAddr' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getMulticastGroups(this.$store.getters.application, params))
      if (error || !response) return

      this.multicastGroups = response.data
    },

    confirmDeleteMulticastGroup (multicastGroup) {
      this.showDeleteMulticastGroupConfirm = true
      this.multicastGroupToDelete = multicastGroup
    },

    async deleteMulticastGroup () {
      let error
      [error] = await to(this.$api.deleteMulticastGroup(this.multicastGroupToDelete))
      if (error) return

      // Success
      eventBus.$emit('message-success', this.$t('a-multicastGroups:deleteSuccess', { multicastGroup: this.multicastGroupToDelete.name }))
      // Refresh list
      this.filter()
    },

    editMulticastGroup (multicastGroup) {
      this.multicastGroupToEdit = multicastGroup
      this.showMulticastGroupForm = true
    }
  }
}
</script>
